import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, } from 'firebase/firestore';
import { db } from '../firebase';
import { Point, Position } from '../utils/types';
import UfoIcon from '../components/flying-object/UfoIcon';
import MagicBackground from '../background/MagicBackground';
import { MIN_PATH_TIME } from '../utils/constants';
import DemoDrawBackground from '../background/demo/DemoDrawBackground';
import { calculateBeePosition } from '../services/beePathUtils';

const pulseKeyframes = `
  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 193, 7, 0.7);
    }
    70% {
      transform: scale(1.1);
      box-shadow: 0 0 0 10px rgba(255, 193, 7, 0);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 193, 7, 0);
    }
  }

  .button-hover {
    transition: all 0.3s ease;
  }

  .button-hover:hover, .button-hover:active {
    animation: pulse 2s infinite;
  }
`;

export default function CreatePath() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [points, setPoints] = useState<Point[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [gameId, setGameId] = useState<string | null>(null);
  const [showLink, setShowLink] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');
  const navigate = useNavigate();
  const startTimeRef = useRef<number>(0);
  const prevPointRef = useRef<Point | null>(null);
  const isDrawingRef = useRef(false);
  const previewRequestRef = useRef<number | null>(null);
  const previewStartTimeRef = useRef<number>(0);
  const [previewBeePosition, setPreviewBeePosition] = useState<Position>({
    x: 0,
    y: 0,
    rotation: 0
  });

  const [showModal, setShowModal] = useState(false);
  const [loseMessage, setLoseMessage] = useState('');
  const [winMessage, setWinMessage] = useState('');
  const [playerName, setPlayerName] = useState(''); // Added state for player name

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  const getCoordinates = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = ('touches' in e ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const startDrawing = (e: React.TouchEvent | React.MouseEvent) => {
    if (points.length > 0 || showPreview) return;
    // Only start drawing on left mouse button (for mouse events)
    if ('button' in e && e.button !== 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
  
    setIsDrawing(true);
    isDrawingRef.current = true;
    const { x, y } = getCoordinates(e);
    startTimeRef.current = Date.now();
    const point = { x, y, time: 0, rotation: 0 };
    prevPointRef.current = point;
    setPoints([point]);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawingRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    const time = Date.now() - startTimeRef.current;
    
    if (ctx && prevPointRef.current) {
      ctx.beginPath();
      ctx.moveTo(prevPointRef.current.x, prevPointRef.current.y);
      ctx.lineTo(x, y);
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  
    const newPoint = {
      x,
      y,
      time,
      rotation: prevPointRef.current ? (Math.atan2(y - prevPointRef.current.y, x - prevPointRef.current.x) * 180 / Math.PI) : 0,
    };
    prevPointRef.current = newPoint;
    setPoints(prev => [...prev, newPoint]);
  };

  const endDrawing = () => {
    setIsDrawing(false);
    isDrawingRef.current = false;
    prevPointRef.current = null;

    // Only show modal if user actually started drawing (not just mouse over)
    if (points.length <= 1) {
      // If user just clicked and released, treat as too short
      setShowModal(true);
      return;
    }

    const totalTime = points.length > 0 ? points[points.length - 1].time : 0;
    if (totalTime < MIN_PATH_TIME && !showModal) {
      setShowModal(true);
      return;
    }
    if (totalTime >= MIN_PATH_TIME) {
      setShowModal(false);
    }
  };

  const handleSubmit = async () => {
    const canvas = canvasRef.current;
    if (!canvas || points.length === 0) return;
  
    const totalTime = points[points.length - 1].time;
    if (totalTime < MIN_PATH_TIME) { // Check if drawing is less than 5 seconds
      setShowModal(true);
      return;
    }
  
    setShowModal(true);
  };

  const handleModalSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas || points.length === 0) return;

    const normalizedPoints = points.map(p => ({
      x: p.x / canvas.width,
      y: p.y / canvas.height,
      time: p.time,
      rotation: p.rotation,
    }));

    const pathData = {
      points: normalizedPoints,
      createdAt: serverTimestamp(),
      totalAttempts: 0,
      successfulAttempts: 0,
      playerName: playerName || null,
      loseMessage: loseMessage || null,
      winMessage: winMessage || null,
      isSinglePlay: !!winMessage, // Single-play condition only for winMessage
    };

    try {
      const docRef = await addDoc(collection(db, 'paths'), pathData);
      setGameId(docRef.id);
      setShowModal(false); // Ensure modal is closed only after successful save
    } catch (error) {
      console.error('Error saving path:', error);
    }
  };

  const handleModalOk = () => {
    clearCanvas(); // Clear the currently drawn path
    setShowModal(false); // Explicitly close the modal
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
  
    // Clear the entire canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Reset the points array
    setPoints([]);
  };

  // Preview animation logic
  useEffect(() => {
    if (!showPreview || points.length === 0) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const animate = () => {
      const elapsed = Date.now() - previewStartTimeRef.current;
      const totalDuration = points[points.length - 1].time;

      const position = calculateBeePosition(points, elapsed, totalDuration, 90);

      setPreviewBeePosition(position);

      previewRequestRef.current = requestAnimationFrame(animate);
    };

    previewStartTimeRef.current = Date.now();
    previewRequestRef.current = requestAnimationFrame(animate);

    return () => {
      if (previewRequestRef.current) {
        cancelAnimationFrame(previewRequestRef.current);
      }
    };
  }, [showPreview, points]);

  const togglePreview = () => {
    setShowPreview(prev => !prev);
  };

  const totalTime = points.length > 0 ? points[points.length - 1].time : 0; // Ensure totalTime is defined

  return (
    <div style={{ touchAction: 'none', height: '100vh' }}>
      <style>{pulseKeyframes}</style>
      <MagicBackground />
      {points.length === 0 && !isDrawing && (
        <>
          <DemoDrawBackground />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              textAlign: 'center',
              zIndex: 11,
              fontSize: '5rem',
              color: '#FFC107',
              textShadow: '0 0 10px rgba(255, 193, 7, 0.7), 0 0 20px rgba(255, 193, 7, 0.5), 0 0 30px rgba(255, 193, 7, 0.3)',
              opacity: 0.5,
              userSelect: 'none', // Prevent text selection
              WebkitUserSelect: 'none',
              msUserSelect: 'none',
              MozUserSelect: 'none',
              pointerEvents: 'none' // Let pointer events pass through
            }}
          >
            Draw your path here
          </div>
        </>
      )}

      <canvas
        ref={canvasRef}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={endDrawing}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={endDrawing}
        onMouseLeave={endDrawing}
        style={{ 
          touchAction: 'none',
          opacity: showPreview ? 0.3 : 1,
          transition: 'opacity 0.3s ease'
        }}
      />

      {showPreview && (
        <div 
          style={{ 
            position: 'absolute',
            left: previewBeePosition.x,
            top: previewBeePosition.y,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 2,
            filter: 'drop-shadow(0 0 8px rgba(255,235,150,0.7))'
          }}
        >
                <UfoIcon 
                  // rotation={previewBeePosition.rotation + 90}
                  emoji="🎯" 
                  ufoType="basic"
                />
        </div>
      )}
      
      {points.length > 0 && !gameId && !isDrawing && (
        <div style={{ 
          position: 'fixed', 
          bottom: 20, 
          left: '50%', 
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxWidth: '500px',
          padding: '0 20px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <button 
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                backgroundColor: showPreview ? '#FF5722' : '#FFC107',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
                boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
                margin: '0 auto 8px'
              }}
              className="button-hover"
              onClick={togglePreview}
            >
              <span style={{ fontSize: '2.5em' }}>{showPreview ? '🔴' : '👁️'}</span>
            </button>
            <div style={{ color: '#FFC107', fontSize: '0.9rem' }}>
              {showPreview ? 'Stop' : 'Preview'}
            </div>
          </div>
          {!showPreview && (
            <>
              <div style={{ textAlign: 'center' }}>
                <button 
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#FFC107',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
                    margin: '0 auto 8px'
                  }}
                  className="button-hover"
                  onClick={clearCanvas}
                >
                  <span style={{ fontSize: '2.5em' }}>☠️</span>
                </button>
                <div style={{ color: '#FFC107', fontSize: '0.9rem' }}>Clear</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <button 
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    backgroundColor: '#FFC107',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
                    margin: '0 auto 8px'
                  }}
                  className="button-hover"
                  onClick={handleSubmit}
                >
                  <span style={{ fontSize: '2.5em' }}>👍🏻</span>
                </button>
                <div style={{ color: '#FFC107', fontSize: '0.9rem' }}>Save</div>
              </div>
            </>
          )}
        </div>
      )}

      {gameId && (
        <div style={{ 
          position: 'fixed', 
          bottom: 20, 
          left: '50%', 
          transform: 'translateX(-50%)', 
          color: '#FFC107',
          textAlign: 'center',
          width: '100%',
          maxWidth: '500px',
          padding: '0 20px'
        }}>
          {showLink && (
            <div style={{ 
              backgroundColor: 'rgba(0,0,0,0.8)', 
              padding: '10px', 
              borderRadius: '10px',
              marginBottom: '20px',
              wordBreak: 'break-all'
            }}>
              <a style={{ margin: 0, color: '#FFC107' }} href={`${window.location.origin}/game/${gameId}`}>{window.location.origin}/game/{gameId}</a>
              {copyFeedback && (
                <p style={{ 
                  margin: '5px 0 0 0',
                  fontSize: '0.9rem',
                  color: copyFeedback === 'Copied!' ? '#4CAF50' : '#FF5722'
                }}>
                  {copyFeedback}
                </p>
              )}
            </div>
          )}
          <div style={{ 
            display: 'flex',
            gap: '20px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`${window.location.origin}/game/${gameId}`);
                    setShowLink(true);
                    setCopyFeedback('Copied!');
                    setTimeout(() => setCopyFeedback(''), 2000);
                  } catch (err) {
                    setShowLink(true);
                    setCopyFeedback('Failed to copy - please copy manually');
                    setTimeout(() => setCopyFeedback(''), 3000);
                  }
                }}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#FFC107',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
                  margin: '0 auto 8px'
                }}
                className="button-hover"
              >
                <span style={{ fontSize: '2.5em' }}>🔗</span>
              </button>
              <div style={{ color: '#FFC107', fontSize: '0.9rem' }}>Copy Link</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button 
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#FFC107',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation',
                  boxShadow: '0 0 0 0 rgba(255, 193, 7, 0.7)',
                  margin: '0 auto 8px'
                }}
                className="button-hover"
                onClick={() => navigate(`/game/${gameId}`)}
              >
                <span style={{ fontSize: '2.5em' }}>🫣</span>
              </button>
              <div style={{ color: '#FFC107', fontSize: '0.9rem' }}>Test Game</div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <>
          {/* Modal Overlay */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          />
          
          {/* Modal Content */}
          <div 
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              padding: '30px',
              borderRadius: '15px',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
              zIndex: 1000,
              color: '#FFC107',
              textAlign: 'center',
              width: '90%',
              maxWidth: '400px',
              animation: 'fadeIn 0.5s ease-in-out'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {totalTime < MIN_PATH_TIME ? (
              <>
                <h3 style={{
                  fontSize: '1.8rem',
                  marginBottom: '20px',
                  textShadow: '0 0 10px rgba(255, 193, 7, 0.7), 0 0 20px rgba(255, 193, 7, 0.5)'
                }}>
                  Minimum Drawing Time Required
                </h3>
                <p style={{
                  marginBottom: '20px',
                  fontSize: '1rem',
                  color: '#FFC107',
                  textShadow: '0 0 5px rgba(255, 193, 7, 0.7)',
                  animation: 'fadeIn 0.5s ease-in-out'
                }}>
                  Your drawing must be at least 5 seconds long. Please redraw your path.
                </p>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleModalOk();
                  }}
                  style={{
                    padding: '10px 20px',
                    fontSize: '1.2rem',
                    backgroundColor: '#FFC107',
                    color: '#000',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                    transition: 'transform 0.2s',
                    width: '100%'
                  }}
                >
                  OK
                </button>
              </>
            ) : (
              <>
                <h3 style={{
                  fontSize: '1.8rem',
                  marginBottom: '20px',
                  textShadow: '0 0 10px rgba(255, 193, 7, 0.7), 0 0 20px rgba(255, 193, 7, 0.5)'
                }}>
                  Add Optional Info..
                </h3>
                <label style={{ display: 'block', marginBottom: '15px' }}>
                  <span style={{ display: 'block', marginBottom: '5px', fontSize: '1.2rem' }}>Name Your Game:</span>
                  <input
                    type="text"
                    value={playerName}
                    onChange={(e) => {
                      e.stopPropagation();
                      setPlayerName(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder='Game Name (Optional)'
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #FFC107',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#FFC107',
                      fontSize: '1rem',
                      outline: 'none',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </label>
                <label style={{ display: 'block', marginBottom: '15px' }}>
                  <span style={{ display: 'block', marginBottom: '5px', fontSize: '1.2rem' }}>Seen After Lose:</span>
                  <input
                    type="text"
                    value={loseMessage}
                    onChange={(e) => {
                      e.stopPropagation();
                      setLoseMessage(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder='Add Message (Optional)'
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #FFC107',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#FFC107',
                      fontSize: '1rem',
                      outline: 'none',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </label>
                <label style={{ display: 'block', marginBottom: '20px' }}>
                  <span style={{ display: 'block', marginBottom: '5px', fontSize: '1.2rem' }}>Seen Only After Win:</span>
                  <input
                    type="text"
                    value={winMessage}
                    onChange={(e) => {
                      e.stopPropagation();
                      setWinMessage(e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder='Add Message (Optional)'
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '8px',
                      border: '1px solid #FFC107',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#FFC107',
                      fontSize: '1rem',
                      outline: 'none',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                </label>
                {winMessage && (
                  <p style={{
                    marginBottom: '20px',
                    fontSize: '1rem',
                    color: '#FFC107',
                    textShadow: '0 0 5px rgba(255, 193, 7, 0.7)',
                    animation: 'fadeIn 0.5s ease-in-out'
                  }}>
                    It's a <span style={{ color: '#E91E63', fontWeight: 'bold' }}>secret message!</span> 😉 This game can now be played only once. Secret fun!
                  </p>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModal(false);
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '1.2rem',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: '#FFC107',
                      border: '1px solid #FFC107',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                      transition: 'transform 0.2s',
                      width: '48%'
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleModalSave();
                    }}
                    style={{
                      padding: '10px 20px',
                      fontSize: '1.2rem',
                      backgroundColor: '#FFC107',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                      transition: 'transform 0.2s',
                      width: '48%'
                    }}
                  >
                    Save Path
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
